import React, { useState, useRef, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Check, Play, FileText, MessageSquare, ArrowLeft, ChevronRight,
  Award, ChevronDown, Lock, Volume2, VolumeX, Maximize, RotateCcw,
  Trophy, X, ArrowRight, BookOpen, Pause, SkipForward, Loader, List
} from "lucide-react";
import Topbar from "../components/Topbar";
import useMobile from "../hooks/useMobile";

const API = process.env.REACT_APP_BACKEND_URL || "";

// ── Quiz Panel (inline, no page change) ──────────────────────────────────────
function QuizPanel({ quiz, courseId, lessonId, onClose, onPass }) {
  const [started, setStarted] = useState(false);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [revealed, setRevealed] = useState(false);
  const [done, setDone] = useState(false);
  const [serverResult, setServerResult] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);

  const q       = quiz.questions[step];
  const total   = quiz.questions.length;
  const isMulti = q?.multi === true;

  const score   = serverResult?.score   ?? 0;
  const percent = serverResult?.percent ?? 0;
  const passed  = percent >= 80;

  const isSelected = (idx) =>
    isMulti ? (answers[q.id] || []).includes(idx) : answers[q.id] === idx;

  const submit = async (explicitAnswers) => {
    const currentAnswers = explicitAnswers ?? answers;
    const ans = currentAnswers[q.id];
    if (ans === undefined) return;
    if (isMulti && (!Array.isArray(ans) || ans.length === 0)) return;
    setRevealed(true);
    if (step === total - 1) {
      setSubmitting(true);
      try {
        const submitUrl = lessonId
          ? `${API}/api/courses/${courseId}/lessons/${lessonId}/quiz/submit`
          : `${API}/api/courses/${courseId}/quiz/submit`;
        const res = await fetch(submitUrl, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ answers: currentAnswers }),
        });
        if (res.ok) setServerResult(await res.json());
      } catch (e) { console.warn("Quiz submit error", e); }
      setSubmitting(false);
    }
  };

  const handleOptionClick = (idx) => {
    if (revealed) return;
    if (isMulti) {
      const current = answers[q.id] || [];
      const next = current.includes(idx) ? current.filter(i => i !== idx) : [...current, idx];
      setAnswers(prev => ({ ...prev, [q.id]: next }));
    } else {
      const newAnswers = { ...answers, [q.id]: idx };
      setAnswers(newAnswers);
      submit(newAnswers);
    }
  };

  const next = () => {
    setRevealed(false);
    if (step < total - 1) setStep(step + 1);
    else setDone(true);
  };
  const restart = () => { setStep(0); setAnswers({}); setRevealed(false); setDone(false); setServerResult(null); };

  const settings   = quiz.settings || {};
  const passMark   = settings.pass_mark_percent || 80;
  const timeLimit  = settings.time_limit_minutes;
  const randQ      = settings.randomise_questions;
  const randA      = settings.randomise_answers;

  const overlay = {
    position: "fixed", inset: 0, zIndex: 50,
    background: "rgba(14,15,18,0.97)",
    display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center",
    padding: "24px",
  };

  const closeBtn = {
    position: "absolute", top: 20, right: 20,
    background: "rgba(255,255,255,0.08)", border: "none",
    borderRadius: "50%", width: 40, height: 40,
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", color: "#fff",
  };

  // ── Intro screen ────────────────────────────────────────────────────────
  if (!started) {
    return (
      <div style={overlay}>
        <button onClick={onClose} style={closeBtn}><X size={18} /></button>
        <div style={{ width: "100%", maxWidth: 560 }}>
          {/* Icon + title */}
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <div style={{
              width: 64, height: 64, borderRadius: "50%", margin: "0 auto 20px",
              background: "rgba(124,92,255,0.15)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <BookOpen size={28} color="#7C5CFF" />
            </div>
            <h2 style={{ fontSize: "clamp(24px,5vw,40px)", fontWeight: 900, letterSpacing: "-0.03em", marginBottom: 8 }}>
              {quiz.title || "Quiz"}
            </h2>
            {quiz.description && (
              <p style={{ fontSize: 15, color: "#9ca3af", lineHeight: 1.7, maxWidth: 480, margin: "0 auto" }}>
                {quiz.description}
              </p>
            )}
          </div>

          {/* Stats grid */}
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 24,
          }}>
            {[
              { label: "Questions", value: total },
              { label: "Pass mark", value: `${passMark}%` },
              { label: "Time limit", value: timeLimit ? `${timeLimit} min` : "None" },
            ].map(({ label, value }) => (
              <div key={label} style={{
                background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 16, padding: "16px 12px", textAlign: "center",
              }}>
                <div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "#6b7280", marginBottom: 6 }}>{label}</div>
                <div style={{ fontSize: 26, fontWeight: 900, color: "#fff" }}>{value}</div>
              </div>
            ))}
          </div>

          {/* Notices */}
          {(randQ || randA) && (
            <div style={{
              background: "rgba(124,92,255,0.08)", border: "1px solid rgba(124,92,255,0.2)",
              borderRadius: 14, padding: "14px 18px", marginBottom: 24,
              display: "flex", flexDirection: "column", gap: 6,
            }}>
              {randQ && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: "#a78bfa" }}>
                  <span style={{ fontSize: 16 }}>⚡</span>
                  Questions are presented in a random order each attempt.
                </div>
              )}
              {randA && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: "#a78bfa" }}>
                  <span style={{ fontSize: 16 }}>⚡</span>
                  Answer options are shuffled per question.
                </div>
              )}
            </div>
          )}

          {/* Start button */}
          <button
            onClick={() => setStarted(true)}
            style={{
              width: "100%", padding: "16px", borderRadius: 99,
              background: "#CFFF50", color: "#0E0F12",
              fontWeight: 800, fontSize: 16, border: "none",
              cursor: "pointer", display: "flex", alignItems: "center",
              justifyContent: "center", gap: 10,
            }}
          >
            Begin Quiz <ArrowRight size={18} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={overlay}>
      {/* Close */}
      <button onClick={onClose} style={closeBtn}><X size={18} /></button>

      <div style={{ width: "100%", maxWidth: 680 }}>
        {!done ? (
          <>
            {/* Header */}
            <div style={{ marginBottom: 32 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <span style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "#7C5CFF", fontWeight: 700 }}>
                  {quiz.title}
                </span>
                <span style={{ fontSize: 13, color: "#6b7280" }}>
                  <span style={{ color: "#fff", fontWeight: 700 }}>{step + 1}</span> / {total}
                </span>
              </div>
              <div style={{ height: 3, background: "rgba(255,255,255,0.08)", borderRadius: 99, overflow: "hidden" }}>
                <div style={{
                  height: "100%", borderRadius: 99,
                  background: "linear-gradient(90deg, #7C5CFF, #CFFF50)",
                  width: `${((step + 1) / total) * 100}%`,
                  transition: "width 0.4s ease"
                }} />
              </div>
            </div>

            {/* Question */}
            <h2 style={{
              fontSize: "clamp(20px, 4vw, 32px)", fontWeight: 900,
              lineHeight: 1.2, letterSpacing: "-0.02em",
              marginBottom: isMulti ? 12 : 32, color: "#fff"
            }}>{q.text}</h2>

            {isMulti && (
              <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 24 }}>
                Select all that apply, then click Submit.
              </p>
            )}

            {/* Options */}
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 32 }}>
              {q.options.map((opt, idx) => {
                const selected = isSelected(idx);
                let borderColor = "rgba(255,255,255,0.1)";
                let bg = "rgba(255,255,255,0.03)";
                if (revealed) {
                  if (selected) { borderColor = "#7C5CFF"; bg = "rgba(124,92,255,0.12)"; }
                  else { bg = "transparent"; borderColor = "rgba(255,255,255,0.04)"; }
                } else if (selected) {
                  borderColor = "#7C5CFF"; bg = "rgba(124,92,255,0.1)";
                }
                return (
                  <button
                    key={idx}
                    disabled={revealed}
                    onClick={() => handleOptionClick(idx)}
                    data-testid={`option-${idx}`}
                    style={{
                      width: "100%", textAlign: "left",
                      padding: "18px 24px", borderRadius: 16,
                      border: `2px solid ${borderColor}`,
                      background: bg,
                      cursor: revealed ? "default" : "pointer",
                      display: "flex", alignItems: "center",
                      justifyContent: "space-between", gap: 16,
                      transition: "all 0.2s ease", color: "#fff",
                      opacity: revealed && !selected ? 0.4 : 1
                    }}
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: 16 }}>
                      {isMulti ? (
                        <span style={{
                          width: 24, height: 24, borderRadius: 6,
                          border: `2px solid ${selected ? "#7C5CFF" : "rgba(255,255,255,0.2)"}`,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          flexShrink: 0,
                          background: selected ? "rgba(124,92,255,0.3)" : "transparent",
                        }}>
                          {selected && <Check size={12} color="#fff" strokeWidth={3} />}
                        </span>
                      ) : (
                        <span style={{
                          width: 32, height: 32, borderRadius: "50%",
                          border: `2px solid ${selected ? "#7C5CFF" : "rgba(255,255,255,0.2)"}`,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 12, fontWeight: 700, flexShrink: 0,
                          color: selected ? "#fff" : "#6b7280"
                        }}>
                          {String.fromCharCode(65 + idx)}
                        </span>
                      )}
                      <span style={{ fontSize: 15, fontWeight: 500 }}>{opt}</span>
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Action */}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
              {isMulti && !revealed && (
                <button
                  onClick={() => submit(answers)}
                  disabled={(answers[q.id] || []).length === 0}
                  data-testid="submit-multi"
                  style={{
                    padding: "14px 32px", borderRadius: 99,
                    background: (answers[q.id] || []).length === 0 ? "rgba(207,255,80,0.3)" : "#CFFF50",
                    color: "#0E0F12",
                    fontWeight: 800, fontSize: 14, border: "none",
                    cursor: (answers[q.id] || []).length === 0 ? "not-allowed" : "pointer",
                  }}
                >
                  Submit answers
                </button>
              )}
              {revealed && (
                <button
                  onClick={next}
                  disabled={submitting}
                  data-testid="next-question"
                  style={{
                    padding: "14px 32px", borderRadius: 99,
                    background: submitting ? "rgba(207,255,80,0.4)" : "#CFFF50",
                    color: "#0E0F12",
                    fontWeight: 800, fontSize: 14, border: "none",
                    cursor: submitting ? "wait" : "pointer",
                    display: "flex", alignItems: "center", gap: 8
                  }}
                >
                  {submitting ? "Marking…" : step < total - 1 ? "Next question" : "See results"} <ArrowRight size={16} />
                </button>
              )}
            </div>
          </>
        ) : (
          /* Results */
          <div style={{ textAlign: "center" }}>
            <div style={{
              width: 96, height: 96, borderRadius: "50%", margin: "0 auto 24px",
              background: passed ? "rgba(207,255,80,0.12)" : "rgba(239,68,68,0.12)",
              display: "flex", alignItems: "center", justifyContent: "center"
            }}>
              <Trophy size={40} color={passed ? "#CFFF50" : "#ef4444"} />
            </div>
            <h2 style={{ fontSize: "clamp(32px,6vw,56px)", fontWeight: 900, letterSpacing: "-0.04em", marginBottom: 12 }}>
              {passed ? "Nice work." : "Keep going."}
            </h2>
            <p style={{ color: "#6b7280", fontSize: 15, marginBottom: 40 }}>
              {passed ? "You passed. Certificate progress updated." : "Score 80% or above to pass. You've got this."}
            </p>

            <div style={{
              display: "inline-flex", gap: 48, padding: "28px 48px",
              borderRadius: 24, border: "1px solid rgba(255,255,255,0.08)",
              background: "rgba(255,255,255,0.03)", marginBottom: 40
            }}>
              {[
                { label: "Score", value: `${score}/${total}` },
                { label: "Percent", value: `${percent}%`, color: passed ? "#CFFF50" : "#ef4444" },
                { label: "Status", value: passed ? "Pass" : "Retry", color: passed ? "#CFFF50" : "#ef4444" }
              ].map((s) => (
                <div key={s.label}>
                  <div style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "#4b5563", marginBottom: 6 }}>{s.label}</div>
                  <div style={{ fontSize: 36, fontWeight: 900, color: s.color || "#fff" }}>{s.value}</div>
                </div>
              ))}
            </div>

            <div style={{ display: "flex", justifyContent: "center", gap: 12 }}>
              <button
                onClick={restart}
                data-testid="retake-quiz"
                style={{
                  padding: "14px 28px", borderRadius: 99,
                  border: "1px solid rgba(255,255,255,0.15)",
                  background: "transparent", color: "#fff",
                  fontWeight: 600, fontSize: 14, cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 8
                }}
              >
                <RotateCcw size={15} /> Retake
              </button>
              <button
                onClick={() => { if (passed) onPass?.(); onClose(); }}
                data-testid="continue-course"
                style={{
                  padding: "14px 28px", borderRadius: 99,
                  background: "#CFFF50", color: "#0E0F12",
                  fontWeight: 800, fontSize: 14, border: "none",
                  cursor: "pointer", display: "flex", alignItems: "center", gap: 8
                }}
              >
                {passed ? "Continue to next lesson" : "Close"} <ArrowRight size={15} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── helpers ───────────────────────────────────────────────────────────────────
const fmtDuration = (secs) => {
  if (!secs) return "";
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

// ── Main Course Player ────────────────────────────────────────────────────────
export default function CoursePlayer() {
  const { courseId } = useParams();

  // ── API state ──
  const [course, setCourse]         = useState(null);
  const [curriculum, setCurriculum] = useState([]);
  const [quiz, setQuiz]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);

  // ── Video URL state ──
  const [embedUrl, setEmbedUrl]           = useState(null);
  const [videoLoading, setVideoLoading]   = useState(false);

  // ── Player state ──
  const [activeIdx, setActiveIdx]         = useState(0);
  const [completedMap, setCompletedMap]   = useState({});
  const [watchPctMap, setWatchPctMap]     = useState({}); // lesson_id → 0-100
  const [lastPosMap, setLastPosMap]       = useState({}); // lesson_id → seconds
  const [tab, setTab]                     = useState("overview");
  const [showQuiz, setShowQuiz]           = useState(false);
  const [expandedStages, setExpandedStages] = useState({});
  const [notes, setNotes]                 = useState("");
  const [videoWatched, setVideoWatched]   = useState({});
  const [quizPassed, setQuizPassed]       = useState({});
  const [certRequested, setCertRequested] = useState(false);
  const [showLessons, setShowLessons] = useState(false);
  const isMobile = useMobile();
  const iframeRef    = useRef(null);
  const playerRef    = useRef(null);
  const lastSaveRef  = useRef({}); // lesson_id → last saved timestamp

  // ── Load course + saved progress ──
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [cRes, progRes, certRes] = await Promise.all([
          fetch(`${API}/api/courses/${courseId}`,                        { credentials: "include" }),
          fetch(`${API}/api/courses/${courseId}/my-progress`,            { credentials: "include" }),
          fetch(`${API}/api/courses/${courseId}/certificate-status`,     { credentials: "include" }),
        ]);
        if (!cRes.ok) throw new Error("Course not found");
        const cData    = await cRes.json();
        const progData = progRes.ok ? await progRes.json() : [];
        const certData = certRes.ok ? await certRes.json() : {};
        if (certData.requested || certData.issued) setCertRequested(true);

        setCourse(cData);
        const stages = cData.curriculum || [];
        setCurriculum(stages);
        setExpandedStages(Object.fromEntries(stages.map((s) => [s.stage, true])));

        // Build maps from saved progress
        const flat = stages.flatMap((s) => s.lessons);
        const completedInit = Object.fromEntries(flat.map((l) => [l.id, false]));
        const pctInit   = {};
        const lastInit  = {};
        const watchInit = {};
        const quizInit  = {};

        for (const row of (progData || [])) {
          if (row.completed) {
            completedInit[row.lesson_id] = true;
            watchInit[row.lesson_id]     = true;
          }
          if (row.quiz_passed) {
            quizInit[row.lesson_id] = true;
          }
          // Restore "mark complete" button if user watched most of a lesson last session
          if ((row.watch_percentage || 0) >= 85) {
            watchInit[row.lesson_id] = true;
          }
          pctInit[row.lesson_id]  = row.watch_percentage  || 0;
          lastInit[row.lesson_id] = row.last_position_seconds || 0;
        }

        setCompletedMap(completedInit);
        setWatchPctMap(pctInit);
        setLastPosMap(lastInit);
        setVideoWatched(watchInit);
        setQuizPassed(quizInit);

        // Resume from last played lesson (find first incomplete)
        const firstIncomplete = flat.findIndex((l) => !completedInit[l.id]);
        if (firstIncomplete > 0) setActiveIdx(firstIncomplete);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [courseId]);

  // ── Fetch signed Bunny URL whenever lesson changes ──
  const flatLessons = curriculum.flatMap((s) =>
    s.lessons.map((l) => ({ ...l, stage: s.stage }))
  );
  const lesson = flatLessons[activeIdx] || null;

  useEffect(() => {
    if (!lesson) return;
    setTab("overview");

    if (lesson.lesson_type === "scorm") {
      // SCORM lesson — build entry URL directly, no Bunny fetch needed
      const entry = lesson.scorm_entry || "index.html";
      setEmbedUrl(`${API}/api/scorm/${lesson.scorm_id}/${entry}`);
      setVideoLoading(false);
      return;
    }

    setEmbedUrl(null);
    setVideoLoading(true);
    fetch(`${API}/api/courses/${courseId}/lessons/${lesson.id}/video-url`, {
      credentials: "include",
    })
      .then((r) => r.ok ? r.json() : Promise.reject("no url"))
      .then((d) => {
        const url = d.embed_url;
        setEmbedUrl(url && !url.includes('playerjs=1') ? url + '&playerjs=1' : url);
      })
      .catch(() => setEmbedUrl(null))
      .finally(() => setVideoLoading(false));
  }, [activeIdx, lesson?.id]);

  // Load Bunny playerjs adapter once
  useEffect(() => {
    if (document.querySelector('script[src*="playerjs"]')) return;
    const s = document.createElement('script');
    s.src = 'https://assets.mediadelivery.net/playerjs/player-0.1.0.min.js';
    s.async = true;
    document.head.appendChild(s);
  }, []);

  // ── SCORM runtime — expose window.API (1.2) and window.API_1484_11 (2004) ──
  // The SCORM content inside the iframe calls window.parent.API / window.parent.API_1484_11.
  // Both are same-origin in production (lms.out2u.com), so this works without postMessage.
  useEffect(() => {
    if (!lesson || lesson.lesson_type !== "scorm") return;

    const scormId = lesson.scorm_id;
    const lessonId = lesson.id;
    let tracked = false;

    const track = async (status, score) => {
      if (tracked) return;
      tracked = true;
      try {
        await fetch(`${API}/api/scorm/${scormId}/track`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ course_id: courseId, lesson_id: lessonId, status, score: score ?? null }),
        });
        if (status === "completed" || status === "passed") {
          setCompletedMap((m) => ({ ...m, [lessonId]: true }));
          setVideoWatched((m) => ({ ...m, [lessonId]: true }));
        }
      } catch (_) { tracked = false; }
    };

    // SCORM 1.2 API
    const data12 = {};
    window.API = {
      LMSInitialize:    ()        => "true",
      LMSFinish:        ()        => { track(data12["cmi.core.lesson_status"] || "completed"); return "true"; },
      LMSGetValue:      (k)       => data12[k] ?? "",
      LMSSetValue:      (k, v)    => {
        data12[k] = v;
        if (k === "cmi.core.lesson_status" && (v === "completed" || v === "passed")) track(v);
        return "true";
      },
      LMSCommit:        ()        => "true",
      LMSGetLastError:  ()        => "0",
      LMSGetErrorString: ()       => "",
      LMSGetDiagnostic: ()        => "",
    };

    // SCORM 2004 API
    const data04 = {};
    window.API_1484_11 = {
      Initialize:       ()        => "true",
      Terminate:        ()        => { track(data04["cmi.completion_status"] || "completed"); return "true"; },
      GetValue:         (k)       => data04[k] ?? "",
      SetValue:         (k, v)    => {
        data04[k] = v;
        if (k === "cmi.completion_status" && v === "completed")  track("completed");
        if (k === "cmi.success_status"    && v === "passed")     track("passed");
        return "true";
      },
      Commit:           ()        => "true",
      GetLastError:     ()        => "0",
      GetErrorString:   ()        => "",
      GetDiagnostic:    ()        => "",
    };

    return () => {
      delete window.API;
      delete window.API_1484_11;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesson?.id, lesson?.lesson_type]);

  // Wire playerjs events when video URL changes
  useEffect(() => {
    if (!embedUrl || !lesson?.id) return;
    const lessonId  = lesson.id;
    const threshold = 90; // % required to count as watched
    let cancelled   = false;
    let retries     = 0;

    const saveProgress = async (pct, posSeconds) => {
      const now = Date.now();
      const last = lastSaveRef.current[lessonId] || 0;
      if (now - last < 10_000) return; // throttle to once per 10 s
      lastSaveRef.current[lessonId] = now;

      setWatchPctMap((prev) => ({ ...prev, [lessonId]: pct }));
      setLastPosMap((prev)  => ({ ...prev, [lessonId]: posSeconds }));

      try {
        await fetch(`${API}/api/courses/${courseId}/progress`, {
          method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            lesson_id:              lessonId,
            watch_percentage:       pct,
            last_position_seconds:  posSeconds,
            completion_threshold:   threshold,
          }),
        });
      } catch (_) {}
    };

    const init = () => {
      if (cancelled || !iframeRef.current || !window.playerjs) return;
      const player = new window.playerjs.Player(iframeRef.current);
      playerRef.current = player;

      player.on('ready', () => {
        if (cancelled) return;

        // Resume from last saved position
        const lastPos = lastPosMap[lessonId] || 0;
        if (lastPos > 5) {
          try { player.setCurrentTime(lastPos); } catch (_) {}
        }

        player.on('ended', () => {
          setVideoWatched((prev) => ({ ...prev, [lessonId]: true }));
          saveProgress(100, 0);
        });

        player.on('timeupdate', ({ seconds, duration }) => {
          if (!duration || duration <= 0) return;
          const pct = Math.round((seconds / duration) * 100);
          // Show "Mark complete" button 5 seconds before end
          if ((duration - seconds) <= 5 && !videoWatched[lessonId]) {
            setVideoWatched((prev) => ({ ...prev, [lessonId]: true }));
          }
          saveProgress(pct, seconds);
        });
      });
    };

    const poll = () => {
      if (cancelled) return;
      if (window.playerjs && iframeRef.current) {
        init();
      } else if (retries < 20) {
        retries++;
        setTimeout(poll, 500);
      }
    };
    setTimeout(poll, 1000);

    return () => { cancelled = true; playerRef.current = null; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedUrl, lesson?.id]);

  const completedCount = Object.values(completedMap).filter(Boolean).length;
  const totalLessons   = flatLessons.length || 1;
  const percent        = Math.round((completedCount / totalLessons) * 100);
  const isLastLesson   = activeIdx === flatLessons.length - 1;

  const markComplete = async () => {
    if (!lesson) return;
    setCompletedMap((m) => ({ ...m, [lesson.id]: true }));
    try {
      await fetch(`${API}/api/enrollments/${courseId}/lessons/complete`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lesson_id: lesson.id }),
      });
    } catch (e) { console.warn("complete error", e); }
  };

  const openQuiz = async () => {
    if (!lesson) return;
    // Try lesson-specific quiz first, fall back to course-level
    let q = null;
    try {
      const r = await fetch(`${API}/api/courses/${courseId}/lessons/${lesson.id}/quiz`, { credentials: "include" });
      if (r.ok) q = await r.json();
    } catch (_) {}
    if (!q) {
      try {
        const r = await fetch(`${API}/api/courses/${courseId}/quiz`, { credentials: "include" });
        if (r.ok) q = await r.json();
      } catch (_) {}
    }
    if (!q || !q.questions?.length) {
      handleQuizPass(lesson.id);
      return;
    }
    setQuiz(q);
    if (playerRef.current) {
      try { playerRef.current.pause(); } catch (_) {}
    }
    setShowQuiz(true);
  };

  const handleQuizPass = async (lessonId) => {
    setQuizPassed(prev => ({ ...prev, [lessonId]: true }));
    // Persist to backend so locking survives page reload
    try {
      await fetch(`${API}/api/courses/${courseId}/lessons/${lessonId}/quiz-pass`, {
        method: "POST", credentials: "include",
      });
    } catch (_) {}
    const nextIdx = activeIdx + 1;
    if (nextIdx < flatLessons.length) setActiveIdx(nextIdx);
  };

  const isLessonLocked = (idx) => {
    if (idx === 0) return false;
    const prev = flatLessons[idx - 1];
    return !quizPassed[prev?.id];
  };

  const toggleStage = (stage) =>
    setExpandedStages((e) => ({ ...e, [stage]: !e[stage] }));

  if (loading) return (
    <div style={{ minHeight: "100vh", background: "#0A0B0E", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Loader size={36} color="#CFFF50" />
    </div>
  );
  if (error || !course) return (
    <div style={{ minHeight: "100vh", background: "#0A0B0E", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff" }}>
      <div style={{ textAlign: "center" }}>
        <p style={{ marginBottom: 16, color: "#6b7280" }}>{error || "Course not found"}</p>
        <Link to="/catalog" style={{ color: "#CFFF50" }}>Back to catalogue</Link>
      </div>
    </div>
  );

  const S = {
    // colour tokens
    bg: "#0A0B0E",
    surface: "#111318",
    surfaceHover: "#161920",
    border: "rgba(255,255,255,0.07)",
    lime: "#CFFF50",
    grape: "#7C5CFF",
    textMuted: "#6b7280",
    textSub: "#9ca3af",
  };

  return (
    <div style={{ minHeight: "100vh", background: S.bg, color: "#fff", fontFamily: "inherit" }}>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      <Topbar searchPlaceholder="Search lessons..." />

      {/* Quiz overlay */}
      {showQuiz && quiz && <QuizPanel quiz={quiz} courseId={courseId} lessonId={lesson?.id} onClose={() => setShowQuiz(false)} onPass={() => handleQuizPass(lesson?.id)} />}

      {/* Top bar */}
      <div style={{
        padding: isMobile ? "10px 14px" : "12px 24px", borderBottom: `1px solid ${S.border}`,
        display: "flex", alignItems: "center", gap: isMobile ? 8 : 16,
        background: S.surface, minWidth: 0
      }}>
        <Link
          to="/my-courses"
          data-testid="back-to-courses"
          style={{
            display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0,
            fontSize: 13, color: S.textMuted, textDecoration: "none",
            transition: "color 0.2s"
          }}
        >
          <ArrowLeft size={15} /> {!isMobile && "My courses"}
        </Link>
        {!isMobile && <span style={{ color: S.border, fontSize: 18 }}>›</span>}
        {!isMobile && (
          <span style={{ fontSize: 13, color: S.textSub, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {course.title}
          </span>
        )}
        {isMobile && (
          <span style={{ fontSize: 13, fontWeight: 600, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
            {course.title}
          </span>
        )}

        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: isMobile ? 8 : 16, flexShrink: 0 }}>
          {/* Progress pill */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {!isMobile && (
              <div style={{ width: 80, height: 4, background: "rgba(255,255,255,0.08)", borderRadius: 99, overflow: "hidden" }}>
                <div style={{
                  height: "100%", borderRadius: 99,
                  background: `linear-gradient(90deg, ${S.grape}, ${S.lime})`,
                  width: `${percent}%`, transition: "width 0.5s ease"
                }} />
              </div>
            )}
            <span style={{ fontSize: 12, fontWeight: 700, color: S.lime }}>{percent}%</span>
          </div>
        </div>
      </div>

      {/* Main layout */}
      <div style={{
        display: isMobile ? "flex" : "grid",
        flexDirection: isMobile ? "column" : undefined,
        gridTemplateColumns: isMobile ? undefined : "1fr 320px",
        height: isMobile ? "auto" : "calc(100vh - 113px)",
        overflowY: isMobile ? undefined : undefined,
      }}>

        {/* ── Left: video + tabs ── */}
        <div style={{ overflowY: isMobile ? undefined : "auto", padding: isMobile ? "14px" : "24px 28px" }}>

          {/* Video / SCORM player */}
          <div style={{
            borderRadius: 20, overflow: "hidden",
            background: "#000",
            aspectRatio: lesson?.lesson_type === "scorm" ? undefined : "16/9",
            minHeight: lesson?.lesson_type === "scorm" ? (isMobile ? 400 : 560) : undefined,
            width: isMobile ? "100%" : "min(100%, calc((100vh - 145px) * 16 / 9))",
            marginBottom: 24,
            border: `1px solid ${S.border}`,
            boxShadow: "0 32px 64px rgba(0,0,0,0.5)"
          }}>
            {videoLoading ? (
              <div style={{
                width: "100%", height: "100%", minHeight: 300, display: "flex",
                alignItems: "center", justifyContent: "center",
                background: "#000"
              }}>
                <Loader size={32} color="#CFFF50" style={{ animation: "spin 1s linear infinite" }} />
              </div>
            ) : embedUrl && lesson?.lesson_type === "scorm" ? (
              // SCORM iframe — plain, no playerjs, same-origin window.parent.API
              <iframe
                key={lesson.id}
                src={embedUrl}
                data-testid="lesson-scorm"
                title={lesson.title}
                allow="accelerometer; autoplay; encrypted-media; fullscreen"
                allowFullScreen
                style={{ width: "100%", height: "100%", minHeight: isMobile ? 400 : 560, border: "none", display: "block" }}
              />
            ) : embedUrl ? (
              // Bunny.net iframe with playerjs
              <iframe
                key={lesson.id}
                ref={iframeRef}
                src={embedUrl}
                data-testid="lesson-video"
                loading="lazy"
                allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
                style={{ width: "100%", height: "100%", border: "none", display: "block" }}
              />
            ) : (
              <div style={{
                width: "100%", height: "100%", minHeight: 300, display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                background: "#0a0b0e", color: "#6b7280"
              }}>
                <Play size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
                <span style={{ fontSize: 14 }}>Video unavailable</span>
              </div>
            )}
          </div>

          {/* Lesson info */}
          <div style={{ marginBottom: 24 }}>
            <div style={{
              fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase",
              color: S.grape, fontWeight: 700, marginBottom: 8
            }}>
              {lesson.stage}
            </div>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
              <div>
                <h1 style={{
                  fontSize: "clamp(20px,3vw,28px)", fontWeight: 900,
                  letterSpacing: "-0.025em", lineHeight: 1.2, marginBottom: 6
                }}>{lesson.title}</h1>
                <p style={{ fontSize: 13, color: S.textMuted }}>
                  {course.title} · {course.instructor_name || "BMG Instructor"} · {lesson.duration}
                </p>
              </div>
              <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
                {completedMap[lesson.id] ? (
                  <button
                    onClick={openQuiz}
                    data-testid="take-quiz-btn"
                    style={{
                      padding: "10px 22px", borderRadius: 99,
                      background: S.grape, color: "#fff",
                      fontWeight: 800, fontSize: 13, border: "none",
                      cursor: "pointer", display: "flex", alignItems: "center", gap: 6
                    }}
                  >
                    <BookOpen size={14} /> Take Quiz
                  </button>
                ) : videoWatched[lesson.id] ? (
                  <button
                    onClick={markComplete}
                    data-testid="mark-complete-btn"
                    style={{
                      padding: "10px 22px", borderRadius: 99,
                      background: S.lime, color: "#0A0B0E",
                      fontWeight: 800, fontSize: 13, border: "none",
                      cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
                      transition: "all 0.2s"
                    }}
                  >
                    <Check size={14} /> Mark complete
                  </button>
                ) : lesson?.lesson_type === "scorm" ? (
                  <div style={{ padding: "10px 0" }}>
                    <span style={{ fontSize: 13, color: S.textMuted }}>Complete the content above to continue</span>
                  </div>
                ) : (
                  <div style={{ padding: "10px 0" }}>
                    <span style={{ fontSize: 13, color: S.textMuted }}>Watch the full video to continue</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div style={{
            borderRadius: 16, border: `1px solid ${S.border}`,
            background: S.surface, overflow: "hidden"
          }}>
            {/* Tab bar */}
            <div style={{
              display: "flex", borderBottom: `1px solid ${S.border}`
            }}>
              {[
                { key: "overview", label: "Overview", icon: Play },
                { key: "notes", label: "Notes", icon: FileText },
              ].map((t) => {
                const Icon = t.icon;
                const active = tab === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => setTab(t.key)}
                    data-testid={`tab-${t.key}`}
                    style={{
                      flex: 1, padding: "14px 16px",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                      fontSize: 13, fontWeight: active ? 700 : 500,
                      color: active ? "#fff" : S.textMuted,
                      background: "transparent", border: "none",
                      cursor: "pointer", position: "relative",
                      transition: "color 0.2s"
                    }}
                  >
                    <Icon size={14} />
                    {t.label}
                    {active && (
                      <span style={{
                        position: "absolute", bottom: 0,
                        left: "50%", transform: "translateX(-50%)",
                        width: 40, height: 2,
                        background: S.lime, borderRadius: 99
                      }} />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Tab content */}
            <div style={{ padding: 24, minHeight: 160, fontSize: 14, color: S.textSub, lineHeight: 1.7 }}>
              {tab === "overview" && (
                <div>
                  {/* What you'll learn — course-level topics */}
                  {course?.topics?.length > 0 && (
                    <div style={{ marginBottom: 20 }}>
                      <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: S.lime, marginBottom: 10 }}>What you'll learn</p>
                      <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
                        {course.topics.map((t, i) => (
                          <li key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                            <Check size={13} style={{ color: S.lime, flexShrink: 0, marginTop: 2 }} />
                            <span style={{ fontSize: 13, color: "#d1d5db" }}>{t}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {/* Lesson description */}
                  {lesson.description && (
                    <div style={{ borderTop: course?.topics?.length > 0 ? `1px solid ${S.border}` : "none", paddingTop: course?.topics?.length > 0 ? 16 : 0 }}>
                      <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: S.textMuted, marginBottom: 8 }}>This lesson</p>
                      <p style={{ whiteSpace: "pre-wrap" }}>{lesson.description}</p>
                    </div>
                  )}
                  {!course?.topics?.length && !lesson.description && (
                    <p style={{ color: S.textMuted }}>No overview available for this lesson.</p>
                  )}
                </div>
              )}
              {tab === "notes" && (
                <textarea
                  data-testid="notes-textarea"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Take notes while you learn..."
                  style={{
                    width: "100%", minHeight: 160,
                    background: "rgba(255,255,255,0.03)",
                    border: `1px solid ${S.border}`,
                    borderRadius: 12, padding: 16,
                    color: "#fff", fontSize: 14, lineHeight: 1.6,
                    resize: "vertical", outline: "none",
                    fontFamily: "inherit", boxSizing: "border-box",
                    transition: "border-color 0.2s"
                  }}
                  onFocus={(e) => e.target.style.borderColor = "rgba(207,255,80,0.4)"}
                  onBlur={(e) => e.target.style.borderColor = S.border}
                />
              )}
            </div>
          </div>

        </div>

        {/* ── Right: lesson sidebar ── */}
        <aside style={{
          borderLeft: isMobile ? "none" : `1px solid ${S.border}`,
          borderTop: isMobile ? `1px solid ${S.border}` : "none",
          background: S.surface,
          overflowY: isMobile ? "hidden" : "auto",
          display: "flex", flexDirection: "column",
        }}>
          {/* Header — acts as toggle on mobile */}
          <div
            style={{ padding: "16px 20px", borderBottom: `1px solid ${S.border}`, cursor: isMobile ? "pointer" : "default" }}
            onClick={isMobile ? () => setShowLessons(s => !s) : undefined}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: isMobile && !showLessons ? 0 : 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <List size={14} color={S.textMuted} />
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: S.textMuted }}>
                  Course contents
                </span>
              </div>
              {isMobile && (
                <ChevronDown size={16} color={S.textMuted} style={{ transition: "transform 0.2s", transform: showLessons ? "rotate(180deg)" : "rotate(0deg)", flexShrink: 0 }} />
              )}
            </div>
            {/* Progress — hide on mobile when collapsed */}
            {(!isMobile || showLessons) && (
              <>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <span style={{ fontSize: 12, color: S.textMuted }}>{completedCount} / {flatLessons.length} lessons</span>
                  <span style={{ fontSize: 12, fontWeight: 800, color: S.lime }}>{percent}%</span>
                </div>
                <div style={{ height: 4, background: "rgba(255,255,255,0.07)", borderRadius: 99, overflow: "hidden" }}>
                  <div style={{
                    height: "100%", borderRadius: 99,
                    background: `linear-gradient(90deg, ${S.grape}, ${S.lime})`,
                    width: `${percent}%`, transition: "width 0.5s ease"
                  }} />
                </div>
              </>
            )}
          </div>

          {/* Curriculum — collapsible on mobile */}
          {(!isMobile || showLessons) && (
          <div style={{ flex: 1, overflowY: "auto", padding: "12px 0" }}>
            {curriculum.map((stage, sIdx) => (
              <div key={sIdx} style={{ marginBottom: 4 }}>
                {/* Stage header */}
                <button
                  onClick={() => toggleStage(stage.stage)}
                  style={{
                    width: "100%", padding: "10px 20px",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    background: "transparent", border: "none",
                    cursor: "pointer", color: S.textMuted
                  }}
                >
                  <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase" }}>
                    {stage.stage}
                  </span>
                  <ChevronDown
                    size={14}
                    style={{ transition: "transform 0.2s", transform: expandedStages[stage.stage] ? "rotate(180deg)" : "rotate(0deg)" }}
                  />
                </button>

                {/* Lessons */}
                {expandedStages[stage.stage] && (
                  <div>
                    {stage.lessons.map((l) => {
                      const idx = flatLessons.findIndex((fl) => fl.id === l.id);
                      const isActive = idx === activeIdx;
                      const isComplete = completedMap[l.id];
                      const locked = isLessonLocked(idx);
                      return (
                        <button
                          key={l.id}
                          onClick={() => !locked && setActiveIdx(idx)}
                          data-testid={`lesson-${l.id}`}
                          style={{
                            width: "100%", padding: "10px 20px",
                            display: "flex", alignItems: "center", gap: 12,
                            background: isActive ? "rgba(207,255,80,0.06)" : "transparent",
                            borderLeft: isActive ? `3px solid ${S.lime}` : "3px solid transparent",
                            border: "none", cursor: locked ? "not-allowed" : "pointer",
                            textAlign: "left", transition: "all 0.15s",
                            opacity: locked ? 0.4 : 1
                          }}
                        >
                          {/* Status dot */}
                          <span style={{
                            width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            background: isComplete ? S.grape : isActive ? S.lime : "rgba(255,255,255,0.06)",
                            border: isComplete || isActive ? "none" : `1px solid rgba(255,255,255,0.12)`
                          }}>
                            {locked
                              ? <Lock size={10} color="rgba(255,255,255,0.4)" />
                              : isComplete
                                ? <Check size={11} color="#fff" />
                                : isActive
                                  ? <Play size={10} color="#0A0B0E" fill="#0A0B0E" />
                                  : null
                            }
                          </span>
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              fontSize: 13, fontWeight: isActive ? 700 : 400,
                              color: isActive ? "#fff" : S.textSub,
                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                              marginBottom: 4
                            }}>{l.title}</div>
                            {locked ? (
                              <div style={{ fontSize: 11, color: S.textMuted }}>Complete previous quiz to unlock</div>
                            ) : l.lesson_type === "scorm" ? (
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: S.lime, textTransform: "uppercase" }}>SCORM</span>
                                <span style={{ fontSize: 11, color: S.textMuted }}>{isComplete ? "· Completed" : ""}</span>
                              </div>
                            ) : (
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                {/* Watch progress bar */}
                                {(watchPctMap[l.id] || 0) > 0 && !isComplete && (
                                  <div style={{ flex: 1, height: 2, background: "rgba(255,255,255,0.08)", borderRadius: 99, overflow: "hidden", maxWidth: 60 }}>
                                    <div style={{
                                      height: "100%", borderRadius: 99, background: S.grape,
                                      width: `${watchPctMap[l.id] || 0}%`, transition: "width 0.5s ease"
                                    }} />
                                  </div>
                                )}
                                <span style={{ fontSize: 11, color: S.textMuted }}>
                                  {isComplete
                                    ? "Completed"
                                    : (watchPctMap[l.id] || 0) > 0
                                      ? `${watchPctMap[l.id]}% watched`
                                      : (l.duration || fmtDuration(lastPosMap[l.id] || 0) || "—")
                                  }
                                </span>
                              </div>
                            )}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
          )}

          {/* Certificate CTA */}
          {percent === 100 && quizPassed[flatLessons[flatLessons.length - 1]?.id] && (
            <div style={{ padding: "16px 20px", borderTop: `1px solid ${S.border}` }}>
              <button
                data-testid="claim-cert-btn"
                disabled={certRequested}
                onClick={async () => {
                  if (certRequested) return;
                  try {
                    await fetch(`${API}/api/courses/${courseId}/request-certificate`, {
                      method: "POST", credentials: "include",
                    });
                    setCertRequested(true);
                  } catch (_) {}
                }}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  gap: 8, padding: "14px 20px", borderRadius: 12, width: "100%",
                  background: certRequested ? "rgba(207,255,80,0.25)" : S.lime,
                  color: certRequested ? S.lime : "#0A0B0E",
                  fontWeight: 800, fontSize: 14, border: "none",
                  cursor: certRequested ? "default" : "pointer",
                  transition: "all 0.2s",
                }}
              >
                <Award size={16} />
                {certRequested ? "Certificate requested ✓" : "Claim your certificate"}
              </button>
            </div>
          )}

        </aside>
      </div>
    </div>
  );
}
